/*
 * Abstract selection component. Base for checkbox, radio and dropdown -selections.
 */
import React from 'react';
import {
  DataType,
  Mode,
  castDataType,
  CONTEXT_OPTIONS
} from '../common.jsx';

import Helpers from './helpers.jsx';
import Setting from './setting.jsx';
import Resolver from '../component_resolver.jsx';
import {resolveAnswer, resolveLookupKeys} from './base.jsx';
import * as FormComponent from '../form_components.jsx';
import { ReadOnlyMultiSelectValues } from '../../common/Select.jsx';
import Option from '../../common/Option.jsx';
import Opt from '../../../hooks/opt.js';


export default class Selection extends FormComponent.Base {
        static get CATEGORY() {
            return 'question';
        }

        // Essentially never used, should probably conber to similar as defaultContent where calculated using super
        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label',
                description: null,
                help: null,
                changeConfirmText: null
            };
        }

        static get DATA_TYPES() {
            return [
                DataType.STRING,
                DataType.INTEGER,
                DataType.DECIMAL
            ];
        }

        static defaultContent(language) {
            let translations = this.defaultContentTranslations(language);
            let options = [
                { value: '1', label: this.generateOptionTranslationKey() }
            ];
            options.forEach((o, i) => {
                translations[language][o.label] = I18n.t('field_default_option', {scope: 'fe', locale: language}) + (i > 0 ? ' ' + (i + 1) : '');
            });
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                description: 'description',
                name: 'field_' + Helpers.randomString(),
                reportKey: '',
                help: 'help',
                required: false,
                label_hidden: false,
                dataType: DataType.STRING,
                lockEditWhenPrefilled: false,
                lockOptionValues: false,
                options: [
                    {
                        dependency: '_default',
                        options: options
                    }
                ],
                inputValidations: [],
                validation_error_message: '',
                changeConfirmText: 'changeConfirmText',
                translations: translations,
                prettyView: {
                    hide: false,
                    show_label: false
                }
            });
        }

        static generateOptionTranslationKey() {
            return 'opt-' + Helpers.randomString(12);
        }

        static addOptions(content, dependencyId, newOptions) {
            (content[Selection.getOptionsKey(content)] || []).forEach((group) => {
                if (group.dependency == dependencyId) {
                    group.options = (group.options || []);
                    newOptions.forEach((o) => {
                        let key = this.generateOptionTranslationKey();
                        group.options.push({
                            value: o.value,
                            label: key
                        });
                        for (let lang in o.label) {
                            content.translations[lang] = content.translations[lang] || {};
                            content.translations[lang][key] = o.label[lang];
                        }
                    });
                    return;
                }
            });
        }


        static setOptionSetKey(content, dependencyId, optionSetKey) {
            content.optionSetKey = optionSetKey;
        }

        static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
          // Use Set to get unique
          let dependencies = new Set(super.resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope) || []);
          content[Selection.getOptionsKey(content)].filter(opt => opt.dependency != '_default').forEach(
            opt => dependencies.add(repeatedFieldScope + '[' + opt.componentId + ']')
          )

          // Hacky way to add both inside and outside the scope to the dependency array
          content.dependencyMapping?.forEach(
            v => {
              dependencies.add(repeatedFieldScope + '[' + v.componentId + ']')
              dependencies.add('[' + v.componentId + ']')
            }   
          );

          // Used to prefill data based on lookup dependency
          if (content.datagridRowLookupDependency) {
            dependencies.add(repeatedFieldScope + '[' + content.datagridRowLookupDependency + ']')
          }

          // Include option set hierarchy parent as an answer dependency
          if (content?.optionSetHierarchyDependentComponent && content?.useOptionSetHierarchy) {
            const hierarchyDependency = repeatedFieldScope + '[' + content.optionSetHierarchyDependentComponent + ']';
            if (hierarchyDependency) {
              dependencies.add(hierarchyDependency);
            }
          }

          return [...dependencies];
        }

        static compatiblePrefillComponents() {
          return ['dropdown_selection'];
        }
      
        static compatiblePrefillDataTypes() {
          return [DataType.INTEGER, DataType.DECIMAL, DataType.STRING];
        }
        

        constructor(props) {
            super(props);

            this.addOption = this.addOption.bind(this);
            this.deleteOption = this.deleteOption.bind(this);
            this.updateOptionOrder = this.updateOptionOrder.bind(this);
            this.validateOptionValue = this.validateOptionValue.bind(this);
            this.toggleOptionGroup = this.toggleOptionGroup.bind(this);
            this.addOptionGroup = this.addOptionGroup.bind(this);
            this.deleteOptionGroup = this.deleteOptionGroup.bind(this);
            this.openOptionLibrary = this.openOptionLibrary.bind(this);
            this.openOptionLibraryAgGridV = this.openOptionLibraryAgGridV.bind(this);
            this.openOptionSetLibraryAgGridV = this.openOptionSetLibraryAgGridV.bind(this);
            this.updateInputValidations = this.updateInputValidations.bind(this);
            this.updateAnswer = this.updateAnswer.bind(this);
            this.confirmShown = false;
            this.state = {
                optionGroupState: JSON.parse(sessionStorage.getItem('adalia-form-builder-option-groups') || '{}'),
                options: [],
                optLoadedLocaleScope: null
            };
            this.options = [];
        }

        async loadOptScope() {
            if (Mode.VIEW != this.props.mode || this.viewModeWholeScope()) {
                const locale = this.props.language;
                const scope = this.props.content?.optionSetKey;
                const hierarchyKey = this.props.content?.optionSetHierarchyKey;

                if (this.useOptionSetHierarchy() && hierarchyKey) {
                    await Opt.load({ hierarchyKey, locale });
                    this.setOptLoadedLocaleScope(locale, hierarchyKey);
                } else {
                    await Opt.load({ scope, locale });
                    this.setOptLoadedLocaleScope(locale, scope);
                }
            }
        }

        // optLoadedLocaleScope is never read: it exists only to flush newly loaded
        // option translations into the view. Skipping the no-op update keeps a
        // redundant loadOptScope call from re-rendering, which would otherwise
        // re-enter componentDidUpdate and loop.
        setOptLoadedLocaleScope(locale, scope) {
            const [loadedLocale, loadedScope] = this.state.optLoadedLocaleScope || [];
            if (loadedLocale === locale && loadedScope === scope) return;

            this.setState({ optLoadedLocaleScope: [locale, scope] });
        }

        async componentDidMount() {
            super.componentDidMount();

            this.loadOptScope();

            if (this.props.mode == Mode.SETTINGS) {
                jQuery(this.refs.settings).find('.options').sortable({
                    axis: 'y',
                    handle: '.sort-handle',
                    update: this.updateOptionOrder
                });
            }

            if (this.props.mode == Mode.FILL) {
                // Store current answer to a string for easy comparison in
                // componentDidUpdate.
                this.currentAnswerStr = JSON.stringify(this.readAnswerFromInputs());
            }
        }

        async componentDidUpdate(prevProps = {}, prevState = {}) {
            super.componentDidUpdate();
            if (Mode.TOOLBAR_ITEM == this.props.mode) return;

            if (prevProps.language !== this.props.language || prevProps.content?.optionSetKey !== this.props.content?.optionSetKey) {
                this.loadOptScope();
            }

            if (Mode.FILL == this.props.mode && this.useDatagridContextual() && !_.isEmpty(this.context.datagridRows)) {
              this.resetIfMissingFromOptions();
            }
        }

        renderView() {
            return (
                <div>
                    {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                            <span className="field-label">{this.getText('label')}</span>
                            {this.props.content.required ? " *" : null}
                            {this.helpIcon()}
                            {this.renderPrefillIcon()}
                        </label>
                        {this.descriptionText()}
                        {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
                        {this.renderOptions()}
                    </>)}
              </div>
            );
        }

        renderPrettyView(){
            let options =  this.getFlatOptions().filter(o => o.value === this.getAnswer()).map(o => {
                    return {label: this.getOptionText(o)};
                });
            const { hide = false, show_label = false } = this.props.content.prettyView || {};
            return ( hide ? null :
                <div>
                    {show_label &&
                        <label>
                            <span className="field-label">{this.getText('label')}</span>
                        </label>
                    }
                    {options.length > 0 ? <ReadOnlyMultiSelectValues values={options} /> : (<p><em>{I18n.t('no_selected_options', {scope: 'fe'})}</em></p>)}
                </div>
            );      
        }

        getAvailableHierarchies() {
            const hierarchies = this.props.optionSetHierarchies;
            return [['', `${I18n.t('select')}...`]].concat(hierarchies.map(h => [h.key, h.label]))
        }

        renderSettings() {
            let dataTypeOptions = this.constructor.DATA_TYPES.map((d) => {
                return [d, I18n.t('data_type_' + d, {scope: 'fe'})];
            });
            let uniqOptionValues = {};
            let hasDuplicateOptionValues = false;
            let duplicateOptionWarning = null;

            let groups = this.getOptionGroups();
            if (this.useOptionLibrary()) {
                groups = [{dependency: '_default', options: []}];
            }

            let optionGroups = groups.map((g, gi) => {
                let isOpenGroup = this.isOpenOptionGroup(g.dependency);
                let headContent = null;
                let optionLibraryButton = null;
                let optionLibraryAgGridVButton = null;
                let optionSetLibraryAgGridVButton = null;
                let groupOptions = this.getOptions(g.dependency);

                let options = groupOptions.map((o, oi) => {
                    if (typeof o.value !== 'undefined' && uniqOptionValues[o.value]) {
                        hasDuplicateOptionValues = true;
                    }
                    uniqOptionValues[o.value] = true;
                    let valueFieldName = 'options|' + g.dependency + '|options|' + oi + '|value';

                    if (isOpenGroup) {
                        let preventChangesOnSafeMode = (g.dependency == '_default' && !this.allowModifyInSafeMode) && !(this.props.content.savedOptionsCount <= oi);

                        return (
                            // The key is formatted this way so that it would change when an option
                            // is deleted forcing the view update.
                            <div key={(o.label || o.value) + '-' + groupOptions.length} data-sort-order={oi} className="option">
                                <Helpers.SortHandle />
                                <div className="option-value truncate-ellipsis">
                                    {this.useOptionLibrary() ? (
                                        <>
                                            <label className="form-control">{o.value}</label>
                                        </>
                                    ) : (
                                        <>
                                            {(this.props.content.lockOptionValues || preventChangesOnSafeMode) && <input type="hidden" name={valueFieldName} defaultValue={o.value} />}
                                            <input
                                              type="text"
                                              name={valueFieldName}
                                              defaultValue={o.value}
                                              data-original-value={o.value}
                                              onBlur={this.validateOptionValue}
                                              onChange={this.updateSettings}
                                              placeholder={I18n.t('option_value', {scope: 'fe'})}
                                              className="form-control"
                                              disabled={(this.props.content.lockOptionValues || preventChangesOnSafeMode)}
                                              autoComplete={this.getAutoComplete()}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="option-label truncate-ellipsis">
                                    {this.useOptionLibrary() ? (
                                        <>
                                            <label className="form-control">{this.renderOption(o)}</label>
                                        </>
                                    ) : (
                                        <>
                                            <input type="hidden" name={'options|' + g.dependency + '|options|' + oi + '|label'} defaultValue={o.label} />
                                            {preventChangesOnSafeMode && <input type="hidden" name={'options|' + g.dependency + '|options|' + oi + '|translated_label'} defaultValue={this.getText(o.label)} />}
                                            <input
                                              type="text"
                                              name={'options|' + g.dependency + '|options|' + oi + '|translated_label'}
                                              defaultValue={this.getText(o.label)}
                                              onChange={this.updateSettings}
                                              placeholder={I18n.t('option_label', {scope: 'fe'})}
                                              className="form-control"
                                              disabled={preventChangesOnSafeMode}
                                              autoComplete={this.getAutoComplete()}
                                            />
                                        </>
                                    )}
                                </div>

                                {this.props.content.useOptionColor &&
                                  this.props.content.enableOptionColor &&
                                    <div className="option-value" style={{marginLeft: '3px'}}>
                                      <input
                                        type="color"
                                        name={'options|' + g.dependency + '|options|' + oi + '|color'}
                                        defaultValue={o.color || 'transparent'}
                                        data-original-value={o.color}
                                        onChange={this.updateSettings}
                                        className="form-control"
                                        disabled={preventChangesOnSafeMode}
                                        autoComplete={this.getAutoComplete()}
                                      />
                                    </div>
                                }

                                {(this.props.content.lockOptionValues || preventChangesOnSafeMode || this.useOptionLibrary()) ? null : <div className="delete">
                                    <a href="#" className="icon-trash" title={I18n.t('delete_option', {scope: 'fe'})} onClick={this.deleteOption}></a>
                                </div>}
                            </div>
                        );
                    } else {
                        return (
                            <div key={oi} className="option">
                                {!this.useOptionLibrary() && <>
                                    <input type="hidden" name={valueFieldName} defaultValue={o.value} data-original-value={o.value}  />
                                    <input type="hidden" name={'options|' + g.dependency + '|options|' + oi + '|label'} defaultValue={o.label} />
                                    <input type="hidden" name={'options|' + g.dependency + '|options|' + oi + '|translated_label'} defaultValue={this.getText(o.label)} />
                                    {this.props.content.useOptionColor &&
                                        this.props.content.enableOptionColor &&
                                        <input type="hidden" name={'options|' + g.dependency + '|options|' + oi + '|color'} defaultValue={o.color} />
                                    }
                                </>}
                            </div>
                        );
                    }
                });

                if (g.dependency == '_default') {
                    headContent = (
                        <div className="group-head">
                            <div className="head">
                                <div className={'toggle' + (isOpenGroup ? ' open' : '')} onClick={this.toggleOptionGroup}></div>
                                <div className="title">
                                    <strong onClick={this.toggleOptionGroup}>
                                        {this.useOptionLibrary()
                                            ? I18n.t('option_set_options', {scope: 'fe'})
                                            : I18n.t('default_options_title', {scope: 'fe'})}
                                        {' '}({options.length})
                                    </strong>
                                </div>
                            </div>
                            <div className="dependency">
                                <input type="hidden" name={'options|_default'} value="" />
                            </div>
                        </div>
                    );
                } else {
                    let questionOptions = this.dependencyComponentOptions().map((o, i) => (
                        <option key={i} value={o[0]}>{o[1]}</option>
                    ));
                    let optionOptions = [];

                    if (isOpenGroup) {
                        optionOptions = this.dependencyOptionOptions(g.componentId || '').map((o, i) => (
                            <option key={i} value={o[0]}>{o[1]}</option>
                        ));
                    }

                    headContent = (
                        <div className="group-head">
                            <div className="head">
                                <div className={'toggle' + (isOpenGroup ? ' open' : '')} onClick={this.toggleOptionGroup}></div>
                                <div className="title">
                                    <strong onClick={this.toggleOptionGroup}>{I18n.t('dependent_options', {scope: 'fe'})} ({options.length})</strong>
                                </div>
                                <div className="delete">
                                    <a href="#" className="icon-trash" title={I18n.t('delete_option_group', {scope: 'fe'})} onClick={this.deleteOptionGroup}></a>
                                </div>
                            </div>
                            {isOpenGroup ? <div className="dependency">
                                <div className="question-link">
                                    <select name={'options|' + g.dependency + '|componentId'} defaultValue={g.componentId || ''} className="form-control" onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
                                        {questionOptions}
                                    </select>
                                </div>
                                <div className="option-link">
                                    <select name={'options|' + g.dependency + '|optionValue'} defaultValue={g.optionValue || ''} className="form-control" onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
                                        {optionOptions}
                                    </select>
                                </div>
                            </div> : <div className="dependency">
                                <input type="hidden" name={'options|' + g.dependency + '|componentId'} defaultValue={g.componentId || ''} />
                                <input type="hidden" name={'options|' + g.dependency + '|optionValue'} defaultValue={g.optionValue || ''} />
                            </div> }
                        </div>
                    );
                }

                if (this.useOptionLibrary() && this.props.optionSetLibraryAgGridVCallback) {
                    optionSetLibraryAgGridVButton = <a href="#" className="btn btn-primary" onClick={this.openOptionSetLibraryAgGridV}><i className="icon-add"></i> {I18n.t('use_option_set', {scope: 'fe'})}...</a>;
                } else if (this.props.optionLibraryAgGridVCallback) {
                    optionLibraryAgGridVButton = <a href="#" className="btn btn-primary" onClick={this.openOptionLibraryAgGridV}><i className="icon-add"></i> {I18n.t('add_from_library', {scope: 'fe'})}...</a>;
                } else if (this.props.optionLibraryCallback) {
                    optionLibraryButton = <a href="#" className="btn btn-primary" onClick={this.openOptionLibrary}><i className="icon-add"></i> {I18n.t('add_from_library', {scope: 'fe'})}...</a>;
                }

                return (<>
                    <div key={g.dependency} className="option-group" data-dependency={g.dependency} style={this.useOptionLibrary() ? {marginBottom: 0} : {}}>
                        {headContent}
                        <div className="group-body" style={{display: isOpenGroup ? 'block' : 'none'}}>
                            <div className="options" data-dependency={g.dependency}>
                                {options}
                            </div>
                            {this.props.content.lockOptionValues ? null : <div className="add-buttons">
                                {!this.useOptionLibrary() && <a href="#" className="btn btn-primary" onClick={this.addOption}><i className="icon-add"></i> {I18n.t('add_option', {scope: 'fe'})}</a>}
                                {optionLibraryButton}
                                {optionSetLibraryAgGridVButton}
                                {optionLibraryAgGridVButton}
                            </div>}
                        </div>
                    </div>
                    {this.useOptionLibrary() && <p className='description'>{I18n.t('option_sets_not_editable_info', {scope: 'fe'})}</p>}
                </>);
            });

            let inputValidationsGroup = this.getFlatOptions().map(item => {
                return {
                    label: this.getText(item.label),
                    value: item.value
                }
            })

            if (hasDuplicateOptionValues) {
                duplicateOptionWarning = (
                    <div className="settings-warning">
                        <i className="fa fa-warning"></i>
                        {' ' + I18n.t('duplicate_option_value_warning', {scope: 'fe'})}
                    </div>
                );
            }

            return (
                <div ref="settings">
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} description={this.props.content.lockOptionValues ? I18n.t('locked_field_label_description', {scope: 'fe'}) : ''} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />

                    {!this.props.settings['hideDescriptionField'] &&
                      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideHelpTextField'] &&
                      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
                    }
                    <Setting.Dropdown
                      name="dataType"
                      label={I18n.t('field_data_type', {scope: 'fe'})}
                      value={this.props.content.dataType}
                      onChange={this.updateSettings}
                      options={dataTypeOptions}
                      disabled={this.props.content.lockOptionValues || !this.allowModifyInSafeMode}
                    />

                    {this.props.externalEntityLinkingCallback &&
                      <Setting.ExternalEntityLinker 
                        name="externalEntityAccessor" 
                        label={I18n.t('field_external_entity_accessor', {scope: 'fe'})} 
                        value={this.props.content.externalEntityAccessor} 
                        onChange={this.updateSettings} 
                        description={I18n.t('field_external_entity_accessor_description', {scope: 'fe'})} 
                        externalEntityLinkingCallback={this.props.externalEntityLinkingCallback}
                        endpoint={this.props.content.endpoint}
                        parent={this.props.content.parentExternalEntityAccessor}
                      />
                    }
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <Setting.Checkbox name="lockEdit" label={I18n.t('field_lock_edit', {scope: 'fe'})} description={I18n.t('field_lock_edit_description', {scope: 'fe'})} value={this.props.content.lockEdit} onChange={this.updateSettings} />
                    {(!this.props.settings['hideLockEditWhenPrefilled'] && AppConfig.features.form_prefill) &&
                      <Setting.Checkbox name="lockEditWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_edit_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockEditWhenPrefilled} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideContainsSensitiveInformation'] &&
                      <Setting.Checkbox
                        name="containsSensitiveInformation"
                        label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
                        description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
                        value={this.props.content.containsSensitiveInformation}
                        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                      />
                    }
                    <Setting.Checkbox
                        name="useOptionLibrary"
                        label={I18n.t('use_option_library', {scope: 'fe'})}
                        description={I18n.t('use_option_library_description', {scope: 'fe'})}
                        value={this.props.content.useOptionLibrary}
                        onChange={this.updateSettings}
                    />

                    {this.useOptionLibrary() && <>
                        <Setting.Checkbox
                            style={{marginBottom: 0}}
                            name="useOptionSetHierarchy"
                            label={I18n.t('use_option_set_hierarchy', {scope: 'fe'})}
                            description={I18n.t('use_option_set_hierarchy_description', {scope: 'fe'})}
                            value={this.props.content.useOptionSetHierarchy}
                            onChange={this.updateSettings}
                        />
                        {this.useOptionSetHierarchy() && <>
                            <Setting.Dropdown
                                style={{paddingTop: 0}}
                                name="optionSetHierarchyKey"
                                label={I18n.t('option_set_hierarchy')}
                                value={this.props.content.optionSetHierarchyKey}
                                onChange={this.updateSettings}
                                options={this.getAvailableHierarchies()}
                            />
                            <Setting.Dropdown
                                style={{paddingTop: 0}}
                                name="optionSetHierarchyDependentComponent"
                                label={I18n.t('option_set_hierarchy_dependent_component', {scope: 'fe'})}
                                value={this.props.content.optionSetHierarchyDependentComponent}
                                onChange={this.updateSettings}
                                options={this.dependencyHierarchyComponentOptions()}
                                description={I18n.t('option_set_hierarchy_dependent_component_description', {scope: 'fe'})}
                            />
                            <div className='setting' style={{paddingTop: 0}}>
                                <label>{I18n.t('level')}</label>
                                <div className='d-flex'>
                                    <input
                                        name='optionSetHierarchyLevel'
                                        type='number'
                                        className='form-control'
                                        value={this.props.content.optionSetHierarchyLevel}
                                        onChange={this.updateSettings}
                                        autoComplete={this.getAutoComplete()}
                                    />
                                </div>
                                <p className='description'>{I18n.t('option_set_hierarchy_level_description', {scope: 'fe'})}</p>
                            </div>
                        </>}
                    </>}
                        {this.props.content.useOptionColor &&
                          <Setting.Checkbox name="enableOptionColor" label={I18n.t('enable_colored_options', {scope: 'fe'})} value={this.props.content.enableOptionColor} onChange={this.updateSettings} />
                        }
                    {!this.useDatagridContextual() &&
                      <>
                        {!this.useOptionSetHierarchy() &&
                        <div className="setting">
                          <label>
                            {I18n.t('field_options', {scope: 'fe'})}
                          </label>
                          {duplicateOptionWarning}
                          <div className="option-groups">
                            {optionGroups}
                          </div>
                          {this.props.content.lockOptionValues || this.useOptionLibrary()
                            ? null
                            : <a href="#" className="btn btn-primary" onClick={this.addOptionGroup}><i className="icon-add"></i> {I18n.t('add_dependent_options', {scope: 'fe'})}</a>}
                        </div>
                        }

                        <div className='setting'>
                          <label>
                            {I18n.t('input_validations', {scope: 'fe'})}
                          </label>
                          <Setting.MultiSelect name="inputValidations" options={inputValidationsGroup} onChange={this.updateInputValidations} value={this.props.content.inputValidations} description={I18n.t('input_validations_description', {scope: 'fe'})} />
                        </div>
                        { this.props.content.inputValidations?.length > 0 &&
                          <Setting.LongTextField name="validation_error_message" label={I18n.t('validation_error_message', {scope: 'fe'})} value={this.props.content.validation_error_message} onChange={this.updateSettings} description={I18n.t('fe')['validation_error_message_description']}  />
                        }
                      </>
                    }

                    {!this.props.settings['hideChangeConfirmText'] &&
                      <Setting.LongTextField 
                        name="changeConfirmText" 
                        label={I18n.t('field_change_confirm_text', {scope: 'fe'})} 
                        value={this.getText('changeConfirmText')} 
                        onChange={this.updateSettings} 
                        description={I18n.t('field_change_confirm_text_description', {scope: 'fe'})}
                      />
                    }
                    {this.renderPrettyViewSettings()}
                </div>
            );
        }

        updateInputValidations(event) {
            let content = JSON.parse(JSON.stringify(this.props.content));
            content['inputValidations'] = event;

            this.props.updateSettingsCallback(content.id, content, true);
        }

        static getOptionsKey(content) {
            return castDataType(content.useOptionLibrary, DataType.BOOLEAN) ? 'optionSetOptions' : 'options';
        }

        getOptionGroups() {
            return (this.props.content[Selection.getOptionsKey(this.props.content)] || []);
        }

        getFlatOptions() {
            return this.getOptions();
        }

        // Whether whole Option translation scope should be loaded. 
        viewModeWholeScope() {
            return this.props.viewMode === FormComponent.ViewMode.PRETTY;
        }

        renderOption(o) {
            // Render for view mode where only the current option is visible (e.g. Dropdown Selection)
            // or label is otherwise not required (React Select with search component for example requires it, so this can't be used)
            if (this.useOptionLibrary() && this.props.mode == Mode.VIEW && !this.viewModeWholeScope()) {
                const value = o?.value ?? o;
                return (
                    <Option
                        scope={this.props.content?.optionSetKey || Opt.scopeFromValue(value)}
                        locale={this.props.language}
                        loadWholeScope={false}
                    >
                        {Opt.unscopedValue(value)}
                    </Option>
                );

            } else {
                return this.getOptionText(o);
            }
        }
        
        getOptionText(o) {
            // Getting labels synchronously for options after option set scope is loaded.
            if (this.useOptionLibrary() && this.useOptionSetHierarchy()) {
                return Opt.t({
                    scope: o.option_set_key || Opt.scopeFromValue(o.value),
                    value: Opt.unscopedValue(o.value),
                    locale: this.props.language
                });
            } else if (this.useOptionLibrary()) {
                return Opt.t({ scope: this.props.content.optionSetKey, value: o.value, locale: this.props.language});
            } else {
                return this.getText(o.label, o.label);
            }
        }

        getHierarchyLinkedComponent() {
            return (this.props.findComponentsCallback?.({
                id: this.props.content.optionSetHierarchyDependentComponent,
                className: 'Selection'
            }) || [])[0];
        }

        getHierarchyLinkedAnswer(answers) {
            if (!this.useOptionSetHierarchy()) return null;

            const linkedComponent = this.getHierarchyLinkedComponent();

            if (!linkedComponent) return null;

            const cClass = Resolver.componentClassName(linkedComponent.type);
            const props = {
                content: linkedComponent,
                answers,
                prefillAnswers: this.props.prefillAnswers
            };

            const componentProps = this.props.repeatedFieldScope
                ? { ...props, repeatedFieldScope: this.props.repeatedFieldScope }
                : props;

            const linkedAnswer = new FormComponent[cClass](componentProps).getRawAnswer(this.context);

            if (!linkedAnswer) return null;

            return Array.isArray(linkedAnswer)
                ? linkedAnswer.map(a => castDataType(a, linkedComponent.dataType))
                : castDataType(linkedAnswer, linkedComponent.dataType);
        }

        getHierarchyOptions() {
            const { content, optionSetHierarchyValues = {} } = this.props;
            const options = optionSetHierarchyValues[content.optionSetHierarchyKey] || [];
            const linkedComponent = this.getHierarchyLinkedComponent();

            const level = isNaN(Number(content.optionSetHierarchyLevel)) ? 0 : Number(content.optionSetHierarchyLevel) - 1;
            const levelOptions = options.filter(o => o.level === level);

            const mapOption = o => ({ value: o.value, label: this.getOptionText(o), level: o.level, option_set_key: o.option_set_key });

            if (!linkedComponent || level === 0) {
                return levelOptions.map(mapOption);
            }

            const linkedAnswer = this.getHierarchyLinkedAnswer(this.props.answers);

            if (!linkedAnswer) return [];
            const linkedAnswers = Array.isArray(linkedAnswer) ? linkedAnswer : [linkedAnswer];

            const parentOptions = options.filter(o => o.level === level - 1 && linkedAnswers.includes(o.value));
            return levelOptions
                .filter(o => parentOptions.some(parentOption => o.parent_value === parentOption.value && o.parent_option_set_key === parentOption.option_set_key))
                .map(mapOption);
        }

        getOptions(dependency) {
            if (this.useDatagridContextual()) return this.getOptionsFromDatagridQuery();

            let options;
            if (this.useOptionSetHierarchy()) {
                options = this.getHierarchyOptions();
            } else if (this.useOptionLibrary()) {
                options = this.props.optionSetValues?.[this.props.content.optionSetKey] || []
            } else if (dependency) {
                options = ((this.props.content[Selection.getOptionsKey(this.props.content)] || []).filter((o) => { return o.dependency == dependency; })[0] || {}).options || [];
            } else {
                options = [];
                this.getActiveDependencies().forEach((d) => {
                    options = options.concat(this.getOptions(d));
                });
            }

            if (this.useOptionLibrary()) {
                const answer = this.getRawAnswer();
                let shownOptions = this.useOptionSetHierarchy() ? options : options.map((o) => ({ value: o.value, label: this.getOptionText(o) }));
                const selectedOptions = Array.isArray(answer) ? answer : [answer];

                if (this.props.mode == Mode.VIEW && selectedOptions.length > 0) {
                    const missingSelectedOptions = selectedOptions
                        .filter((selected) => selected != null && !shownOptions.some((o) => o.value === selected))
                        .map((selected) => ({ value: selected, label: this.getOptionText({value: selected}) }));
                    shownOptions = shownOptions.concat(missingSelectedOptions);
                }

                return shownOptions
            }

            return options;
        }

        // Which of the dependent option groups should be shown on form?
        getActiveDependencies() {
            let dependencies = ['_default'];

            if (this.props.findComponentsCallback) {
                this.getOptionGroups().forEach((group) => {
                    if ((group.componentId || '') != '') {
                        let linkedComponent = this.props.findComponentsCallback({id: group.componentId})[0];

                        if (linkedComponent) {
                            let cClass = Resolver.componentClassName(linkedComponent.type);
                            let props = {
                                content: linkedComponent,
                                answers: this.props.answers,
                                prefillAnswers: this.props.prefillAnswers
                            };
                            let linkedAnswer = null;

                            // Try finding answer inside a shared repeated field scope or top level
                            if (this.props.repeatedFieldScope) {
                                linkedAnswer = (new FormComponent[cClass](Object.assign({repeatedFieldScope: this.props.repeatedFieldScope}, props))).getRawAnswer(this.context);
                            }
                            if (!linkedAnswer) {
                                linkedAnswer = (new FormComponent[cClass](props)).getRawAnswer(this.context);
                            }

                            // The answer can be an array in case of multiple selection
                            if (Array.isArray(linkedAnswer)) {
                                linkedAnswer = linkedAnswer.map((a) => (castDataType(a, linkedComponent.dataType)));
                            }
                            if (linkedAnswer == group.optionValue || (Array.isArray(linkedAnswer) && linkedAnswer.indexOf(group.optionValue) != -1)) {
                                dependencies.push(group.dependency);
                            }
                        }
                    }
                });
            }
            return dependencies;
        }

        // Overwrite in child component to change behaviour 
        selectionCleared(currentValue, newValue) {
          return !(currentValue == null || currentValue == '')
        }

        updateAnswer (value) {
          const selectionCleared = this.selectionCleared(this.getAnswer(), value);

          const confirmText = this.getText('changeConfirmText')

          if (this.confirmShown || !selectionCleared || !confirmText || confirmText == '') {
            return super.updateAnswer(value)
          }

          this.confirmShown = true;
          if (confirm(confirmText)) {
            return super.updateAnswer(value)
          }
        }

        getFormattedPrefillAnswer() {
            return this.getOptions().filter(o => this.getPrefilledAnswer().includes(o.value)).map(o => this.getText(o.label)).join(", ");
        }

        toggleOptionGroup(event) {
            let el = jQuery(event.currentTarget).closest('.option-group').find('.toggle');
            let group = el.closest('.option-group');
            let groupKey = this.props.content.id + '-' + group.data('dependency');

            let optionGroupState = Object.assign({}, this.state.optionGroupState);

            if (el.is('.open')) {
                optionGroupState[groupKey] = false;
            } else {
                optionGroupState[groupKey] = true;
            }
            sessionStorage.setItem('adalia-form-builder-option-groups', JSON.stringify(optionGroupState));

            group.addClass('loading');
            setTimeout((() => {
                this.setState({
                    optionGroupState: optionGroupState
                }, () => {
                    group.removeClass('loading');
                });
            }).bind(this), 1);

            event.preventDefault();
        }

        isOpenOptionGroup(dependency) {
            return this.state.optionGroupState?.[this.props.content.id + '-' + dependency];
        }

        addOptionGroup(event) {
            // let content = {id: this.props.content.id, translations: JSON.parse(JSON.stringify(this.props.content.translations))};
            let content = JSON.parse(JSON.stringify(this.props.content));

            let dependency = 'dep-' + Helpers.randomString(16);

            this.readOptionsFromInputs(content);

            [['value', this.getNextOptionValue()], ['label', this.constructor.generateOptionTranslationKey()], ['translated_label', '']].forEach((attr) => {
                content['options|' + dependency + '|options|0|' + attr[0]] = attr[1];
            });

            // Copy componentId from previous group
            content['options|' + dependency + '|componentId'] = (this.props.content[Selection.getOptionsKey(this.props.content)][this.props.content[Selection.getOptionsKey(this.props.content)].length - 1] || {}).componentId || '';

            this.beforeUpdateSettings(content);
            this.props.updateSettingsCallback(content.id, content, true);

            // New group shall be open by default
            let optionGroupState = Object.assign({}, this.state.optionGroupState);
            optionGroupState[this.props.content.id + '-' + dependency] = true;
            this.setState({
                optionGroupState: optionGroupState
            });

            event.preventDefault();
        }

        addOption(event) {
            let content = JSON.parse(JSON.stringify(this.props.content));
            let dependency = jQuery(event.currentTarget).closest('.option-group').data('dependency');
            let nextOptionIndex = this.getOptions(dependency).length;
            if (content.savedOptionsCount == undefined && dependency == '_default') {
                content.savedOptionsCount = nextOptionIndex;
            }

            this.readOptionsFromInputs(content);

            [['value', this.getNextOptionValue()], ['label', this.constructor.generateOptionTranslationKey()], ['translated_label', '']].forEach((attr) => {
                content['options|' + dependency + '|options|' + nextOptionIndex + '|' + attr[0]] = attr[1];
            });
            
            this.beforeUpdateSettings(content);
            this.props.updateSettingsCallback(content.id, content, true);

            event.preventDefault();
        }

        getNextOptionValue() {
            var usedValues = [];

            jQuery(this.refs[this.settingsFormRef()]).find('.option-value input').each(function(o) {
                usedValues.push(jQuery(this).val());
            });

            var value = '1';

            for (var i = 1; i <= jQuery(this.refs[this.settingsFormRef()]).find('.option-value input').length + 1; i++) {
                if (usedValues.indexOf(String(i)) == -1) {
                    value = String(i);
                    break;
                }
            }
            return value;
        }

        deleteOptionGroup(event) {
            if (confirm(I18n.t('confirm_delete_option_group', {scope: 'fe'}))) {
              let content = JSON.parse(JSON.stringify(this.props.content))
              let deletedOptionGroup = jQuery(event.currentTarget).closest('.option-group');

                this.readOptionsFromInputs(content, deletedOptionGroup);

                this.beforeUpdateSettings(content);
                this.props.updateSettingsCallback(content.id, content, true);
            }
            event.preventDefault();
        }

        deleteOption(event) {
            if (confirm(I18n.t('confirm_delete_option', {scope: 'fe'}))) {
                let content = JSON.parse(JSON.stringify(this.props.content))
                let deletedOption = jQuery(event.currentTarget).closest('.option');

                this.readOptionsFromInputs(content, null, deletedOption);
                this.beforeUpdateSettings(content);

                // Remove empty slot from options caused by the deletion
                content[Selection.getOptionsKey(content)].forEach((group) => {
                    group.options = group.options.filter(o => o != undefined);
                });

                this.props.updateSettingsCallback(content.id, content, true);
            }
            event.preventDefault();
        }

        readOptionsFromInputs(content, deletedOptionGroup, deletedOption) {
            jQuery(this.refs[this.settingsFormRef()]).find('.option-group').not(deletedOptionGroup).each((i, group) => {
                group = jQuery(group);

                group.find('.dependency').find('input, select').serializeArray().forEach((s) => {
                    content[s.name] = s.value;
                });

                group.find('.option').not(deletedOption).find('input').serializeArray().forEach((s) => {
                    content[s.name] = s.value;
                });
            });
        }

        updateOptionOrder(event) {
            let sortContainer = jQuery(event.target);
            let dependency = sortContainer.attr('data-dependency');
            let oldOptions = this.getOptions(dependency);
            let newOptions = [];
            sortContainer.sortable('toArray', {attribute: 'data-sort-order'}).forEach((i) => {
                newOptions.push(Object.assign({}, oldOptions[parseInt(i, 10)]));
            });

            let content = {id: this.props.content.id, options: []};

            JSON.parse(JSON.stringify(this.props.content)).options.forEach((o) => {
                if (o.dependency == dependency) {
                    o.options = newOptions;
                }
                content[Selection.getOptionsKey(content)].push(o);
            });
            this.props.updateSettingsCallback(content.id, content, true);
        }

        beforeUpdateSettings(content) {
            let originalOptions = Object.assign({}, content[Selection.getOptionsKey(content)]);
            content[Selection.getOptionsKey(content)] = [];
            const saveOptions = !this.useOptionLibrary();

            // Delete old option translations
            content.translations[this.props.language] = content.translations[this.props.language] || {};
            Object.keys(content.translations[this.props.language]).forEach((k) => {
                if (k.match(/^opt\-/) && saveOptions) {
                    delete content.translations[this.props.language][k];
                }
            });

          if (saveOptions) {
            Object.keys(content).filter((k) => { return k.match(/^options\|/); }).forEach((k) => {
                let parts = k.split('|'); // options|dependency|attr|index|attr
                let optGroupIndex = -1;
                content[Selection.getOptionsKey(content)].some((o, i) => { if (o.dependency == parts[1]) { optGroupIndex = i; return true; }; });

                if (optGroupIndex == -1) {
                    optGroupIndex = content[Selection.getOptionsKey(content)].length;
                    content[Selection.getOptionsKey(content)].push({dependency: parts[1], options: []});
                }

                if (parts[2] == 'options') {
                    let oIndex = parseInt(parts[3], 10);

                    if (parts[4] == 'translated_label') {
                        content.translations[this.props.language][content[k.replace(/translated_label$/, 'label')] || content[Selection.getOptionsKey(content)][optGroupIndex].options[oIndex].label] = content[k];
                    } else {
                        content[Selection.getOptionsKey(content)][optGroupIndex].options[oIndex] = content[Selection.getOptionsKey(content)][optGroupIndex].options[oIndex] || {};
                        content[Selection.getOptionsKey(content)][optGroupIndex].options[oIndex][parts[4]] = (parts[4] == 'value') ? castDataType(content[k] || originalOptions[optGroupIndex].options[oIndex].value, content.dataType) : content[k];
                    }

                } else if (parts[2] == 'componentId' || parts[2] == 'optionValue') {
                    content[Selection.getOptionsKey(content)][optGroupIndex][parts[2]] = content[k] || '';

                    // Cast optionValue to match data type of the linked component
                    if ((content[Selection.getOptionsKey(content)][optGroupIndex].componentId || '') != '' && (content[Selection.getOptionsKey(content)][optGroupIndex].optionValue || '') != '') {
                        let linkedC = this.props.findComponentsCallback({id: content[Selection.getOptionsKey(content)][optGroupIndex].componentId})[0];
                        content[Selection.getOptionsKey(content)][optGroupIndex].optionValue = linkedC ? castDataType(content[Selection.getOptionsKey(content)][optGroupIndex].optionValue, linkedC.dataType) : '';
                    }
                }

                delete content[k];
            });
          }

          content['optionSetOptions'] = [{ dependency: '_default', options: [] }];

          content.required = typeof content.required == 'boolean' ? content.required : castDataType(content.required, DataType.BOOLEAN);
          content.label_hidden = typeof content.label_hidden == 'boolean' ? content.label_hidden : castDataType(content.label_hidden, DataType.BOOLEAN);
          content.lockEditWhenPrefilled = typeof content.lockEditWhenPrefilled == 'boolean' ? content.lockEditWhenPrefilled : castDataType(content.lockEditWhenPrefilled, DataType.BOOLEAN);
          content.useOptionLibrary = typeof content.useOptionLibrary == 'boolean' ? content.useOptionLibrary : castDataType(content.useOptionLibrary, DataType.BOOLEAN);
          content.useOptionSetHierarchy = typeof content.useOptionSetHierarchy == 'boolean' ? content.useOptionSetHierarchy : castDataType(content.useOptionSetHierarchy, DataType.BOOLEAN);
          content.lockEdit = typeof content.lockEdit == 'boolean' ? content.lockEdit : castDataType(content.lockEdit, DataType.BOOLEAN);
          content.useOptionColor = typeof content.useOptionColor == 'boolean' ? content.useOptionColor : castDataType(content.useOptionColor, DataType.BOOLEAN);
          content.enableOptionColor = typeof content.enableOptionColor == 'boolean' ? content.enableOptionColor : castDataType(content.enableOptionColor, DataType.BOOLEAN);

          if (super.isDatagridContextAvailable()) {
            content.datagridId = castDataType(content.datagridId, DataType.INTEGER);
            content.presetId = castDataType(content.presetId, DataType.INTEGER);
            content.columnAccessor = castDataType(content.columnAccessor, DataType.STRING);
            content.dependencyMapping = []

            Object.keys(content).filter((k) => { return k.match(/^dependencyMapping\|/); }).forEach((k) => {
                let parts = k.split('|'); // dependencyMapping|colId|index|attr

                if (parts[3] == 'componentId') {
                    content.dependencyMapping[parts[2]] ||= {}
                    content.dependencyMapping[parts[2]]['colId'] = parts[1]
                    content.dependencyMapping[parts[2]][parts[3]] = content[k] || '';
                }
                delete content[k];
            });
          }
          if (super.isDatagridContextAvailable() && this.useDatagridContextual()) {
            let { datagridId, columnAccessor } = content;
            const selectedColumn = this.context.datagridContext?.find(v => v.id == datagridId)?.columns?.find(v => v.accessor == columnAccessor);

            // For option set columns, persist the same settings used by standard option set dropdowns
            // so that translation scopes are loaded and values loaded from datagrid are rendered correctly.
            if (selectedColumn?.column_type === 'option_set') {
                content.useOptionLibrary = true;
                content.useOptionSetHierarchy = selectedColumn?.use_option_set_hierarchy === true;
                if (content.useOptionSetHierarchy) {
                    content.optionSetHierarchyKey = selectedColumn?.option_set_hierarchy_key;
                } else {
                    content.optionSetKey = selectedColumn?.option_set_key;
                }
            } else {
                content.useOptionLibrary = false;
                content.useOptionSetHierarchy = false;
                content.optionSetHierarchyKey = null;
                content.optionSetKey = null;
            }
          }

          this.beforeUpdateSettingsPrettyView(content);
        }

        validateOptionValue(event) {
            let input = jQuery(event.currentTarget);

            if (input.val() == '') {
                input.val(input.data('original-value'));
                alert(I18n.t('error_form_option_value_required'));
            }
        }

        dependencyComponentOptions() {
            let options = [['', I18n.t('dependent_question_placeholder', {scope: 'fe'})]];

            if (this.props.findComponentsCallback) {
                this.props.findComponentsCallback({className: 'Selection'}).forEach((c) => {
                    // Exclude self and those components that depend on self (to avoid circular references)
                    if (c.id != this.props.content.id && (c.options || []).map((g) => (g.componentId)).indexOf(this.props.content.id) == -1) {
                        let label = Helpers.getText(c.translations, c.label, this.props.language);
                        options.push([
                            c.id,
                            (label != '') ? label : (c.reportKey || '' != '') ? c.reportKey : '[ ' + I18n.t('no_label_or_key', {scope: 'fe'}) + ' ]'
                        ]);
                    }
                });
            }
            return options;
        }

        dependencyOptionOptions(componentId) {
            let options = [['', I18n.t('dependent_option_placeholder', {scope: 'fe'})]];

            if (this.props.findComponentsCallback) {
                this.props.findComponentsCallback({id: componentId}).forEach((c) => {
                    c.options.forEach((group) => {
                        group.options.forEach((o) => {
                            let label = Helpers.getText(c.translations, o.label, this.props.language);
                            options.push([
                                o.value,
                                (label != '') ? label : '[ ' + I18n.t('no_label', {scope: 'fe'}) + ' ]'
                            ]);
                        });
                    });
                });
            }
            return options;
        }

        dependencyHierarchyComponentOptions() {
            let options = [['', `${I18n.t('select')}...`]];

            if (this.props.findComponentsCallback) {
                this.props.findComponentsCallback({className: 'Selection'}).forEach((c) => {
                    // Exclude self and those components that depend on self (to avoid circular references)
                    const usingHierarchy = c.useOptionSetHierarchy === true || castDataType(c.useOptionSetHierarchy, DataType.BOOLEAN);
                    if (
                        c.id != this.props.content.id &&
                        usingHierarchy &&
                        c.optionSetHierarchyKey === this.props.content.optionSetHierarchyKey && // Same hierarchy
                        (c.options || []).map((g) => (g.componentId)).indexOf(this.props.content.id) == -1
                    ) {
                        let label = Helpers.getText(c.translations, c.label, this.props.language);
                        options.push([
                            c.id,
                            (label != '') ? label : (c.reportKey || '' != '') ? c.reportKey : '[ ' + I18n.t('no_label_or_key', {scope: 'fe'}) + ' ]'
                        ]);
                    }
                });
            }
            return options;
        }


        openOptionLibrary(event) {
            let el = jQuery(event.currentTarget);
            this.props.optionLibraryCallback(jQuery(this.refs.root).data('id'), el.closest('.option-group').data('dependency'));

            event.preventDefault();
        }

        openOptionLibraryAgGridV(event) {
            let el = jQuery(event.currentTarget);
            this.props.optionLibraryAgGridVCallback(jQuery(this.refs.root).data('id'), el.closest('.option-group').data('dependency'));

            event.preventDefault();
        }

        openOptionSetLibraryAgGridV(event) {
            let el = jQuery(event.currentTarget);
            this.props.optionSetLibraryAgGridVCallback(jQuery(this.refs.root).data('id'), el.closest('.option-group').data('dependency'));
            event.preventDefault();
        }

        resetIfMissingFromOptions() {
          if (this.props.mode != Mode.FILL) return;
          // DATAGRID_ROW values are derived from row data, not chosen from options — skip.
          if (this.props.content.useContextual === CONTEXT_OPTIONS.DATAGRID_ROW) return;
          if (this.getRawAnswer() == this.rawAnswerDefault()) return;

          if (!this.options.some( opt => opt.value == this.getRawAnswer())) {
            this.updateAnswer(this.rawAnswerDefault());
          }
        }

        getOptionsFromDatagridQuery() {
          if (![Mode.FILL, Mode.VIEW].includes(this.props.mode)) return [];

          if (this.props.content.useContextual === CONTEXT_OPTIONS.DATAGRID_ROW) {
            return this._getOptionsFromDatagridRow();
          }
          return this._getOptionsFromDatagridPreset();
        }

        // DATAGRID_ROW: 1-to-1 — render the single option corresponding to the column
        // value in the currently selected row. datagridId/presetId come from the linked
        // DatagridRowLookup component, not from this component's content.
        _getOptionsFromDatagridRow() {
          const { columnAccessor } = this.props.content;
          const lookupComp = this.props.findComponentsCallback?.({id: this.props.content.datagridRowLookupDependency})?.[0];
          const datagridId = lookupComp?.datagridId;
          const selectedColumn = this.context.datagridContext?.find(v => v.id == datagridId)?.columns?.find(v => v.accessor == columnAccessor);

          const colValue = this.getLinkedComponentAnswer();
          if (colValue == null) return [];

          let options;
          if (selectedColumn?.is_selection && selectedColumn?.selection_options) {
            const opt = selectedColumn.selection_options.find(o => o.value == colValue);
            options = opt ? [opt] : [{value: colValue, label: colValue}];
          } else if (selectedColumn?.is_selection) {
            // EXTERNAL_COLUMN_TYPES: column value is {id, label}
            options = typeof colValue === 'object'
              ? [{value: colValue.id, label: colValue.label}]
              : [{value: colValue, label: colValue}];
          } else {
            options = [{value: colValue, label: colValue}];
          }

          this.options = options;
          return options;
        }

        // DATAGRID_PRESET: 1-to-many — load all options from rows matching the
        // dependencyMapping. datagridId/presetId are defined on this component.
        _getOptionsFromDatagridPreset() {
          let {datagridId, presetId, columnAccessor, dependencyMapping = []} = this.props.content;
          let rows = this.context?.datagridRows?.[datagridId]?.[presetId]?.rows || [];
          let selectedColumn = this.getSelectedDatagridColumn();

          let options = [];
          let lookupBy = resolveLookupKeys(dependencyMapping, this.props.fieldScope, this.props.repeatedFieldScope, this.props.answers);
          if (lookupBy == undefined) return options;

          const valueGetter = row => typeof row === 'object' ? row?.value || row?.id : row;

          const removeDuplicateByValue = (arr) => {
            const uniqueMap = new Map();
            for (const item of arr) {
              if (!uniqueMap.has(item.value)) uniqueMap.set(item.value, item);
            }
            return Array.from(uniqueMap.values());
          };

          const limitToValuesFromRows = rows
            .filter(row => Object.keys(lookupBy).every(colId => valueGetter(row[colId]) == lookupBy[colId]))
            .map(r => r[columnAccessor])
            .filter(Boolean);

          if (selectedColumn?.is_selection && selectedColumn?.column_type === 'option_set') {
            options = removeDuplicateByValue(limitToValuesFromRows.map(v => ({value: v})));
          } else if (selectedColumn?.is_selection && selectedColumn?.selection_options) {
            options = selectedColumn.selection_options;
            // Rows are not loaded in other modes than fill. We can have all as options to render correctly
            if (this.props.mode == Mode.FILL) {
              options = options.filter(opt => limitToValuesFromRows.includes(opt.value));
            }
          // Hate this spaghetti condition,
          // but is_selection is true and selection_options are null for
          // DatagridCol::EXTERNAL_COLUMN_TYPES. Need to resolve the options from the rows
          } else if (selectedColumn?.is_selection) {
            if (this.props.mode == Mode.FILL) {
              options = removeDuplicateByValue(
                limitToValuesFromRows.map(v => ({value: v.id, label: v.label}))
              );
            } else if (super.getAnswer()) {
              options = [{label: this.getStoredLabel(), value: super.getAnswer()}];
            }
          // Non-select column as dropdown, list all.
          } else {
            options = limitToValuesFromRows.map(v => ({value: v, label: v}));
          }

          const currentAnswer = this.getRawAnswer();
          if (!options.some(opt => opt.value == currentAnswer)) {
            if (this.props.mode != Mode.FILL) {
              // Selected option has lost its mapping (removed from datagrid);
              // add as option to still display in view mode.
              options.push({value: currentAnswer, label: currentAnswer});
            }
          }
          this.options = options;
          return options;
        }

        getLinkedComponentAnswer() {
            let answer = null;
            if (this.props.findComponentsCallback && this.props.content.datagridRowLookupDependency) {
                const comp = this.props.findComponentsCallback({id: this.props.content.datagridRowLookupDependency})[0]
                const dependencyComponentName = comp.name;
                const {linkedAnswerWithinRegion, linkedAnswerOutside} = resolveAnswer(this.props.fieldScope, this.props.repeatedFieldScope, dependencyComponentName, this.props.answers);
                answer = linkedAnswerWithinRegion || linkedAnswerOutside
                answer = answer?.[this.props.content.columnAccessor];
            }
            return answer
        }
}
